/**
 * RFP Report Templates
 * Pre-configured report templates for RFP proposal generation
 */

import React from 'react';
import { ReportQuestion } from '@tracekit/core';
import { FileText, FileEdit, Target, CheckCircle, Search, Briefcase, Calendar } from 'lucide-react';

export interface ReportTemplate {
  id: string;
  type: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  questions: ReportQuestion[];
  regulatoryContext: {
    authorities: string[];
    deadline: string;
    frequency: string;
    submissionTo: string[];
  };
}

export const RFP_REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'standard_proposal',
    type: 'rfp_proposal',
    name: 'Standard RFP Proposal',
    description: 'Comprehensive proposal template covering all standard RFP sections including executive summary, technical approach, past performance, and project team',
    icon: FileText,
    color: 'from-blue-500 to-blue-600',
    questions: [
      { section: 'Executive Summary', question: 'Provide a compelling executive summary that highlights our key qualifications, approach, and value proposition for this RFP opportunity. Include project understanding, our competitive advantages, and commitment to success.' },
      { section: 'Company Overview', question: 'Describe Keystone Environmental Ltd., including our history, mission, core competencies, certifications, and relevant experience in environmental consulting and project management.' },
      { section: 'Technical Approach', question: 'Detail our technical approach to addressing the RFP requirements. Include methodology, tools, technologies, quality assurance processes, and how we will ensure project success.' },
      { section: 'Past Performance', question: 'Present relevant past performance examples that demonstrate our capability to deliver similar projects. Include project descriptions, outcomes, client references, and lessons learned.' },
      { section: 'Project Team', question: 'Introduce the proposed project team, including key personnel roles, qualifications, certifications (e.g., P.Eng., P.Geo.), relevant experience, and their specific contributions to this project.' },
      { section: 'Schedule and Deliverables', question: 'Provide a detailed project schedule with milestones, deliverables, timelines, and resource allocation. Demonstrate our ability to meet or exceed the RFP deadline requirements.' },
      { section: 'Budget and Pricing', question: 'Present our budget breakdown, pricing structure, cost justification, and value proposition. Ensure compliance with RFP pricing requirements and demonstrate cost-effectiveness.' },
      { section: 'Risk Management', question: 'Identify potential project risks and our mitigation strategies. Include quality assurance measures, contingency planning, and how we will ensure project success despite challenges.' },
    ],
    regulatoryContext: {
      authorities: ['RFP Issuing Agency', 'Client Organization'],
      deadline: 'As specified in RFP',
      frequency: 'Per RFP opportunity',
      submissionTo: ['RFP Coordinator', 'Evaluation Committee'],
    },
  },
  {
    id: 'environmental_assessment',
    type: 'rfp_proposal',
    name: 'Environmental Assessment Proposal',
    description: 'Specialized template for environmental impact assessments, site investigations, and remediation projects',
    icon: Briefcase,
    color: 'from-green-500 to-green-600',
    questions: [
      { section: 'Executive Summary', question: 'Provide an executive summary highlighting our environmental assessment expertise, understanding of the project scope, and our approach to delivering comprehensive environmental services.' },
      { section: 'Project Understanding', question: 'Demonstrate understanding of the environmental assessment requirements, regulatory context, site conditions, and stakeholder considerations. Show alignment with client objectives.' },
      { section: 'Technical Methodology', question: 'Detail our technical methodology for environmental assessment including sampling strategies, analytical approaches, data management, quality assurance/quality control (QA/QC), and reporting frameworks.' },
      { section: 'Regulatory Compliance', question: 'Explain our approach to ensuring regulatory compliance with applicable environmental regulations, permits, and guidelines. Include experience with relevant regulatory agencies and compliance frameworks.' },
      { section: 'Site Investigation Approach', question: 'Describe our site investigation approach including field methods, sampling protocols, laboratory analysis, data interpretation, and risk assessment methodologies.' },
      { section: 'Remediation Strategy', question: 'If applicable, present our remediation strategy including technology selection, implementation approach, monitoring programs, and success criteria. Demonstrate cost-effectiveness and sustainability.' },
      { section: 'Team Qualifications', question: 'Highlight team qualifications including environmental scientists, engineers, geologists, and specialists. Include relevant certifications (P.Eng., P.Geo., EP, etc.) and project experience.' },
      { section: 'Past Environmental Projects', question: 'Present relevant past environmental assessment and remediation projects. Include project scope, challenges overcome, outcomes achieved, and client testimonials.' },
      { section: 'Health and Safety', question: 'Describe our health and safety program, site-specific safety plans, worker training, and commitment to zero-incident performance. Include relevant safety certifications and track record.' },
      { section: 'Schedule and Budget', question: 'Provide detailed project schedule with key milestones and realistic budget estimates. Demonstrate ability to deliver quality results within timeline and budget constraints.' },
    ],
    regulatoryContext: {
      authorities: ['Provincial Environmental Agencies', 'Federal Environmental Agencies', 'Municipal Authorities'],
      deadline: 'As specified in RFP',
      frequency: 'Per RFP opportunity',
      submissionTo: ['RFP Coordinator', 'Environmental Manager', 'Evaluation Committee'],
    },
  },
  {
    id: 'compliance_monitoring',
    type: 'rfp_proposal',
    name: 'Compliance Monitoring Proposal',
    description: 'Template for ongoing compliance monitoring, reporting, and regulatory submission projects',
    icon: CheckCircle,
    color: 'from-purple-500 to-purple-600',
    questions: [
      { section: 'Executive Summary', question: 'Provide executive summary highlighting our compliance monitoring expertise, understanding of regulatory requirements, and our systematic approach to ensuring ongoing compliance.' },
      { section: 'Regulatory Framework Understanding', question: 'Demonstrate understanding of applicable regulatory frameworks, permit conditions, reporting requirements, and compliance obligations. Show expertise with relevant agencies.' },
      { section: 'Monitoring Program Design', question: 'Detail our monitoring program design including parameters, sampling frequency, locations, analytical methods, quality assurance, and data management systems.' },
      { section: 'Reporting and Documentation', question: 'Describe our reporting approach including report formats, submission schedules, data presentation, trend analysis, and communication with regulatory agencies.' },
      { section: 'Data Management', question: 'Explain our data management systems including database design, quality control, validation procedures, accessibility, and long-term data preservation.' },
      { section: 'Compliance Assurance', question: 'Detail our approach to ensuring compliance including early warning systems, exceedance response procedures, corrective action protocols, and continuous improvement processes.' },
      { section: 'Team and Resources', question: 'Present the monitoring team, their qualifications, certifications, and availability. Include laboratory partnerships, equipment, and technical resources.' },
      { section: 'Past Compliance Projects', question: 'Showcase relevant past compliance monitoring projects including duration, complexity, regulatory interactions, and successful outcomes.' },
      { section: 'Cost Structure', question: 'Provide transparent cost structure including setup costs, ongoing monitoring costs, reporting costs, and any variable costs. Demonstrate value and cost-effectiveness.' },
    ],
    regulatoryContext: {
      authorities: ['Regulatory Compliance Agencies', 'Permit Issuing Authorities'],
      deadline: 'As specified in RFP',
      frequency: 'Per RFP opportunity',
      submissionTo: ['Compliance Manager', 'Regulatory Coordinator', 'Evaluation Committee'],
    },
  },
  {
    id: 'quick_response',
    type: 'rfp_proposal',
    name: 'Quick Response Proposal',
    description: 'Streamlined template for urgent RFP responses with essential sections only',
    icon: Target,
    color: 'from-orange-500 to-orange-600',
    questions: [
      { section: 'Executive Summary', question: 'Provide a concise executive summary highlighting our qualifications and approach to this opportunity.' },
      { section: 'Technical Approach', question: 'Describe our technical approach to meeting the RFP requirements.' },
      { section: 'Key Qualifications', question: 'Highlight our key qualifications, relevant experience, and team capabilities.' },
      { section: 'Budget Summary', question: 'Present our budget summary and pricing structure.' },
    ],
    regulatoryContext: {
      authorities: ['RFP Issuing Agency'],
      deadline: 'As specified in RFP',
      frequency: 'Per RFP opportunity',
      submissionTo: ['RFP Coordinator'],
    },
  },
  {
    id: 'custom_proposal',
    type: 'rfp_proposal',
    name: 'Custom Proposal',
    description: 'Build a custom proposal with your own sections and questions tailored to specific RFP requirements',
    icon: FileEdit,
    color: 'from-gray-500 to-gray-600',
    questions: [
      { section: 'Introduction', question: 'Provide an introduction to our proposal and understanding of the RFP requirements.' },
    ],
    regulatoryContext: {
      authorities: ['RFP Issuing Agency'],
      deadline: 'As specified in RFP',
      frequency: 'Per RFP opportunity',
      submissionTo: ['RFP Coordinator'],
    },
  },
];










